import React, { useState } from 'react';
import { View, TouchableOpacity, StyleSheet, Text as RNText, Modal, ActivityIndicator } from 'react-native';
import { Text } from '@/components';
import { Image } from 'expo-image';
import { Dog, Plus, X, UserPlus } from 'lucide-react-native';
import { useAuth } from '@/context/AuthContext';
import { useDogOwners, useAddDogOwner, useRemoveDogOwner, useMyGroups } from '@/lib/queries/groups';
import theme from '@/constants/colors';
import { Typography } from '@/constants/typography';
import { useTranslation } from 'react-i18next';

export default function DogOwnersSection({ dogId }: { dogId: string }) {
  const { t } = useTranslation();
  const { session, user } = useAuth();
  const token = session?.accessToken;
  const ownersQuery = useDogOwners(token, dogId);
  const owners = ownersQuery.data ?? [];
  const familiesQuery = useMyGroups(token, 'family');
  const family = (familiesQuery.data ?? [])[0];
  const familyMembers = (family?.members ?? []).filter((m) => m.userId !== user?.id && !owners.some((o) => o.userId === m.userId));

  const addOwner = useAddDogOwner(token);
  const removeOwner = useRemoveDogOwner(token);

  const primary = owners.find((o) => o.role === 'primary');
  const isPrimary = primary?.userId === user?.id;
  const isCoOwner = owners.some((o) => o.role === 'co-owner' && o.userId === user?.id);
  const canManage = isPrimary; // only primary adds; primary removes co-owners; co-owner can leave
  const [addOpen, setAddOpen] = useState(false);

  const handleAdd = (userId: string) => {
    addOwner.mutate({ dogId, userId }, { onSuccess: () => setAddOpen(false) });
  };

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>{t('groups.coOwners')}</Text>
      {ownersQuery.isLoading ? (
        <ActivityIndicator color={theme.accentPrimary} />
      ) : (
        owners.map((o) => (
          <View key={o.userId} style={styles.row}>
            {o.image ? (
              <Image source={o.image} style={styles.avatar} contentFit="cover" cachePolicy="memory-disk" />
            ) : (
              <View style={[styles.avatar, styles.avatarPlaceholder]}><Dog size={16} color={theme.textMuted} /></View>
            )}
            <Text style={styles.name}>{o.name || o.userId}</Text>
            <Text style={styles.role}>{o.role === 'primary' ? t('groups.owner') : t('groups.coOwner')}</Text>
            {(o.role === 'primary' ? false : (isPrimary || o.userId === user?.id)) && (
              <TouchableOpacity onPress={() => removeOwner.mutate({ dogId, userId: o.userId })} hitSlop={12}>
                <X size={18} color={theme.textMuted} />
              </TouchableOpacity>
            )}
          </View>
        ))
      )}

      {isPrimary && (
        <TouchableOpacity style={styles.addBtn} onPress={() => setAddOpen(true)}>
          <Plus size={18} color={theme.accentPrimary} />
          <Text style={styles.addBtnText}>{t('groups.addCoOwner')}</Text>
        </TouchableOpacity>
      )}
      {isCoOwner && (
        <Text style={styles.hint}>{t('groups.coOwnerHint')}</Text>
      )}

      {/* Add co-owner from family members */}
      <Modal visible={addOpen} transparent animationType="slide" onRequestClose={() => setAddOpen(false)}>
        <TouchableOpacity style={styles.overlay} onPress={() => setAddOpen(false)} activeOpacity={1}>
          <View style={styles.sheet}>
            <Text style={styles.sheetTitle}>{t('groups.addCoOwner')}</Text>
            {!family ? (
              <Text style={styles.hint}>{t('groups.needFamilyFirst')}</Text>
            ) : familyMembers.length === 0 ? (
              <Text style={styles.hint}>{t('groups.noFamilyCandidates')}</Text>
            ) : (
              familyMembers.map((m) => (
                <TouchableOpacity key={m.userId} style={styles.row} onPress={() => handleAdd(m.userId)}>
                  {m.image ? (
                    <Image source={m.image} style={styles.avatar} contentFit="cover" cachePolicy="memory-disk" />
                  ) : (
                    <View style={[styles.avatar, styles.avatarPlaceholder]}><Dog size={16} color={theme.textMuted} /></View>
                  )}
                  <Text style={styles.name}>{m.name || m.userId}</Text>
                  <UserPlus size={18} color={theme.accentPrimary} />
                </TouchableOpacity>
              ))
            )}
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  section: { marginTop: 16 },
  sectionTitle: { ...Typography.label(theme.textMuted), marginBottom: 10 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 8, backgroundColor: theme.backgroundSecondary, borderRadius: 12, paddingHorizontal: 12, marginVertical: 3 },
  avatar: { width: 32, height: 32, borderRadius: 16 },
  avatarPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: theme.backgroundSecondaryVarient },
  name: { ...Typography.body(theme.textPrimary), flex: 1, fontSize: 15 },
  role: { ...Typography.caption(theme.textMuted), fontSize: 12, marginRight: 8 },
  addBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, padding: 12, borderWidth: 1, borderColor: theme.borderSubtle, borderRadius: 12, marginTop: 8, alignSelf: 'flex-start' },
  addBtnText: { ...Typography.body(theme.accentPrimary), fontSize: 14, fontWeight: '600' },
  hint: { ...Typography.caption(theme.textMuted), fontSize: 12, marginTop: 6 },
  overlay: { flex: 1, justifyContent: 'flex-end', backgroundColor: 'rgba(0,0,0,0.45)' },
  sheet: { backgroundColor: theme.backgroundPrimary, borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: 20, maxHeight: '70%' },
  sheetTitle: { ...Typography.body(theme.textPrimary), fontSize: 18, fontWeight: '700', marginBottom: 16 },
});